/**
 * Zustand der Geräteanmeldung (Kassenschlüssel) für alle API-Abrufe der Kasse.
 *
 * Meldet der Server „Anmeldung ungültig“ (401 mit `anmeldungUngueltig`, z. B. nach einer
 * Neuausstellung oder einem Widerruf des Schlüssels), hält die Kasse alle Abrufe mit diesem
 * Schlüssel an, statt alle paar Sekunden weiter anzufragen: Jeder weitere Versuch wäre ein
 * Fehlversuch und könnte die Adresssperre des Servers auslösen. Erst ein anderer Schlüssel
 * (Einrichtung) oder ein ausdrückliches „Erneut prüfen“ gibt die Abrufe wieder frei. Der
 * Schlüssel wird nie automatisch gelöscht oder ersetzt.
 *
 * Bei 429 (Adresssperre) wartet die Kasse die vom Server genannte Zeit ab.
 */
export type AnmeldeZustand =
  { art: 'ok' } | { art: 'ungueltig'; seit: number } | { art: 'wartet'; bis: number };

export const ANMELDUNG_UNGUELTIG_TEXT =
  'Geräteanmeldung ungültig – automatische Abfragen sind angehalten. Ein Administrator stellt im Backoffice unter „Kassen“ einen neuen Kassenschlüssel aus; diesen unter „Einrichtung“ eintragen und „Verbinden“ wählen.';

/** Sperrgrund für einen Abruf oder null, wenn er laufen darf. */
export interface AnmeldeSperre {
  status: 401 | 429;
  meldung: string;
}

export class AnmeldeWaechter {
  private zustand: AnmeldeZustand = { art: 'ok' };
  /** Schlüssel, mit dem der Fehler auftrat – ein anderer Schlüssel hebt die Sperre auf. */
  private schluesselBeiFehler: string | null = null;
  private readonly hoerer = new Set<(z: AnmeldeZustand) => void>();

  constructor(private readonly jetzt: () => number = () => Date.now()) {}

  aktuell(): AnmeldeZustand {
    return this.zustand;
  }

  /** Darf mit diesem Schlüssel angefragt werden? */
  sperre(schluessel: string | null): AnmeldeSperre | null {
    const z = this.zustand;
    if (z.art === 'ok') {
      return null;
    }
    if (schluessel !== this.schluesselBeiFehler) {
      this.setze({ art: 'ok' });
      return null;
    }
    if (z.art === 'ungueltig') {
      return { status: 401, meldung: ANMELDUNG_UNGUELTIG_TEXT };
    }
    const rest = z.bis - this.jetzt();
    if (rest <= 0) {
      this.setze({ art: 'ok' });
      return null;
    }
    return {
      status: 429,
      meldung: `Server meldet zu viele ungültige Anmeldungen von diesem Anschluss – nächster Versuch in ${String(Math.ceil(rest / 1000))} Sekunden.`,
    };
  }

  /** Antwort des Servers auswerten (nur Anmeldefehler des Servers zählen). */
  melde(
    status: number,
    schluessel: string | null,
    antwort: Readonly<Record<string, unknown>>,
    retryAfterSekunden: number | null,
  ): void {
    if (schluessel === null) {
      return; // ohne Schlüssel (Ersteinrichtung) gibt es nichts anzuhalten
    }
    if (status === 401 && antwort.anmeldungUngueltig === true) {
      this.schluesselBeiFehler = schluessel;
      this.setze({ art: 'ungueltig', seit: this.jetzt() });
    } else if (status === 429) {
      // Ein nachlaufendes 429 ersetzt „ungültig“ desselben Schlüssels nicht durch eine Wartezeit.
      if (this.zustand.art === 'ungueltig' && schluessel === this.schluesselBeiFehler) {
        return;
      }
      const sekunden =
        retryAfterSekunden !== null && retryAfterSekunden > 0
          ? Math.min(retryAfterSekunden, 3600)
          : 60;
      this.schluesselBeiFehler = schluessel;
      this.setze({ art: 'wartet', bis: this.jetzt() + sekunden * 1000 });
    }
  }

  /**
   * Ausdrücklich erneut versuchen (Knopf „Erneut prüfen“): der nächste Abruf darf wieder laufen.
   * Eine verspätete Erfolgsantwort hebt eine Sperre dagegen nie auf – nur ein anderer Schlüssel,
   * der Ablauf der Wartezeit oder dieser Knopf.
   */
  erneutPruefen(): void {
    this.setze({ art: 'ok' });
  }

  abonniere(f: (z: AnmeldeZustand) => void): () => void {
    this.hoerer.add(f);
    return () => {
      this.hoerer.delete(f);
    };
  }

  private setze(z: AnmeldeZustand): void {
    this.zustand = z;
    for (const f of this.hoerer) {
      f(z);
    }
  }
}
